import { useQuery } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectPotentialFranchiseId,
  setBestSlotAdjustment,
  setPrepayDiscountPercent,
} from '../features/booking/bookingSlice';
import { GET_BOOKING_PRICING_CONFIG } from '../util/gql';

/**
 * Keeps the booking's pricing rules (prepay percentage, best slot
 * discount, tax, the mobile service fee rules) in step with the server, so
 * every step shows the number the booking will be created with. Moved out
 * of routes/Booking.tsx unchanged; the consumer skin's shell runs it too.
 */
export default function useBookingPricingConfig(): void {
  const dispatch = useAppDispatch();
  const potentialFranchiseId = useAppSelector(selectPotentialFranchiseId);
  const { serviceAddress } = useAppSelector((s) => s.serviceLocation);
  // The visit fee can depend on how many visits the vehicle already had this
  // plan year (Aperio), so the config is asked for per vehicle.
  // and per catalogue vehicle (a medium-duty truck pays its own fee).
  const { userVehicleId, id: vehicleId } = useAppSelector((s) => s.car);

  useQuery(GET_BOOKING_PRICING_CONFIG, {
    variables: {
      franchiseId: potentialFranchiseId || null,
      countryCode: serviceAddress?.countryCode || null,
      provinceCode: serviceAddress?.provinceCode || null,
      postalCode: serviceAddress?.postalCode || null,
      userVehicleId: userVehicleId || null,
      vehicleId: vehicleId || null,
    },
    // Nothing on step 1 reads it, and without an address every variable is
    // null: the answer would be discarded as soon as one is picked (QA
    // 2026-09-25 #52, getBookingPricingConfig asked 3x, first with nulls).
    skip: !serviceAddress?.postalCode,
    onCompleted: (data) => {
      const config = data?.getBookingPricingConfig;
      if (typeof config?.prepayDiscountPercent === 'number') {
        dispatch(setPrepayDiscountPercent(config.prepayDiscountPercent));
      }
      dispatch(setBestSlotAdjustment({
        percent: config?.bestSlotAdjustmentPercent ?? 0,
        label: config?.bestSlotAdjustmentLabel ?? null,
        cutoffHours: config?.discountCutoffHours ?? null,
        taxRatePercent: config?.taxRatePercent ?? null,
        mobileServiceFeeCents: config?.mobileServiceFeeCents ?? null,
        mobileServiceFeeAllInclusiveCents:
          config?.mobileServiceFeeAllInclusiveCents ?? null,
        mobileServiceFeeHighUseCents:
          config?.mobileServiceFeeHighUseCents ?? null,
        mobileServiceFeeVisitsIncluded:
          config?.mobileServiceFeeVisitsIncluded ?? null,
        planYearVisitCount: config?.planYearVisitCount ?? null,
        planStartMobileFeeIncluded: !!config?.planStartMobileFeeIncluded,
      }));
    },
  });
}
